// Studby — authentication context.
// Holds the current user, exposes login / signup / logout, and knows whether
// the user still has access (active subscription or unexpired trial).
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null while checking
  const [loading, setLoading] = useState(true);

  // On mount: if a token exists, fetch the current user.
  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("studly_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      localStorage.removeItem("studly_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("studly_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("studly_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("studly_token");
    setUser(false);
  };

  // Re-fetch the user (e.g. after subscribing).
  const refreshUser = loadUser;

  // Does the user currently have access to premium features?
  const hasAccess =
    !!user && ["active", "trialing"].includes(user.subscription_status);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, refreshUser, hasAccess }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
